import React, { createContext, useEffect, useReducer, useState } from 'react';
import { signInWithCustomToken, signInWithEmailAndPassword } from 'firebase/auth';
import axios from 'axios';
import { accountTradeApi, firebaseCustomApi, getMe } from '../../api/account';
import { AuthClient } from '../../grpc/generated/auth_grpc_web_pb';
import { TokenRequest } from '../../grpc/generated/auth_pb';
import { Notification } from '../../utilities/alert';
import { auth, logoutFirebase } from '../../firebase';
import { StorageService } from './storageService';
import { serverApiConfig } from '../../config';
import { handleTokenResponse } from './authService';
import ListArray from '../../data/listArray';
import RegulationHelper from '../../helpers/regulationHelper';
import { getUserRegulation } from '../../utilities/firestoreUtils';
let google_protobuf_empty_pb = require('google-protobuf/google/protobuf/empty_pb.js');

let accountTradeData = null;

const initialState = {
  isAuthenticated: false,
  isInitialized: true,
  user: null,
  metatrader: [],
  activeAccount: null,
  accActiveData: null,
  isOtp: false,
  isLoading: false,
  listArray: []
};

const handlers = {
  LOADING: (state, action) => {
    const { isLoading } = action.payload;
    return {
      ...state,
      isLoading
    };
  },
  INITIALIZE: (state, action) => {
    const {
      isAuthenticated,
      user,
      metatrader,
      isInitialized,
      activeAccount,
      accActiveData,
      isOtp,
      isLoading,
      listArray
    } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized,
      user,
      metatrader,
      activeAccount,
      accActiveData,
      isOtp,
      isLoading,
      listArray
    };
  },
  LOGIN: (state, action) => {
    const {
      user,
      metatrader,
      isInitialized,
      activeAccount,
      isOtp,
      isLoading,
      listArray,
      accActiveData
    } = action.payload;
    return {
      ...state,
      isAuthenticated: false,
      isInitialized,
      user,
      metatrader,
      activeAccount,
      accActiveData,
      isOtp,
      isLoading,
      listArray
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isInitialized: false,
    user: null,
    metatrader: null,
    activeAccount: null,
    accActiveData: null,
    isOtp: false,
    isLoading: false,
    listArray: []
  }),
  REGISTER: (state, action) => {
    const { user, metatrader } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      metatrader
    };
  },
  AUTHTOKEN: (state, action) => {
    const {
      user,
      metatrader,
      isInitialized,
      activeAccount,
      setToken,
      isOtp,
      isLoading,
      listArray,
      accActiveData
    } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isInitialized,
      user,
      metatrader,
      activeAccount,
      accActiveData,
      setToken,
      isOtp,
      isLoading,
      listArray
    };
  }
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  authWithToken: () => Promise.resolve(),
  getTokenFromUrl: () => Promise.resolve(),
  getFirebaseCustom: () => Promise.resolve(),
  reState: () => Promise.resolve()
});

function AuthProvider({ children }) {
  const request = new google_protobuf_empty_pb.Empty();
  const lisArray = new ListArray();
  const tokenRequest = new TokenRequest();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [index, setIndex] = useState(0);
  const [location, setLocation] = useState(null);
  const handleAuthError = async (error) => {
    if (error.response?.data?.code === 1010) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          ...initialState,
          isInitialized: false,
          isOtp: true
        }
      });
    } else {
      Notification({
        message: error.response?.data?.error || 'Authentication failed',
        title: 'Invalid',
        type: 'danger'
      });
      await logout();
    }
  };

  const handleTradeData = async (user, tradeData) => {
    if (!tradeData?.data.length) {
      Notification({
        message: 'Account trade not found',
        title: 'Invalid',
        type: 'danger'
      });
      dispatch({
        type: 'INITIALIZE',
        payload: {
          ...initialState,
          isInitialized: false
        }
      });
      dispatch({
        type: 'LOADING',
        payload: {
          isLoading: false
        }
      });
      return;
    }

    const token = await user.getIdToken();
    await initialize(token, tradeData);
  };

  const handleTokenError = async (error) => {
    Notification({
      message: 'Token request failed',
      type: 'danger'
    });

    return {
      isAuthenticated: false,
      isInitialized: false,
      metatrader: null,
      activeAccount: null,
      accActiveData: null
    };
  };

  const getFirebaseCustom = async () => {
    let token = null;
    try {
      const { data } = await firebaseCustomApi();
      token = data.data.token;
      return token;
    } catch (error) {
      return;
    }
  };

  const getTokenFromAuth = async (working, token, data) => {
    const authClients = working.map((account) => ({
      client: new AuthClient(serverApiConfig(account?.api_server), null, null),
      account
    }));

    let count = 0;

    // Try each account sequentially until one succeeds
    for (const { client, account } of authClients) {
      try {
        tokenRequest.setMainToken(token);
        tokenRequest.setAccountTradeId(account.id);

        const response = await new Promise((resolve, reject) => {
          client.getToken(tokenRequest, {}, (err, response) => {
            if (err) reject(err);
            else resolve(response);
          });
        });

        // If successful, process the response
        const state = await handleTokenResponse(response, working, working.indexOf(account), data);
        return state;
      } catch (error) {
        if (working.length - 1 === count++) {
          Notification({
            title: `Something went wrong`,
            message: error.message,
            type: 'danger'
          });
        }
        continue;
      }
    }

    // If all accounts fail, return unauthenticated state
    return {
      isAuthenticated: false,
      isInitialized: false,
      metatrader: [],
      activeAccount: null,
      accActiveData: null,
      listArray: []
    };
  };

  const initialize = async (token, data) => {
    const tk = StorageService.getItem('trade.token');
    const server = StorageService.getItem('trade.server');
    const acc = StorageService.getItem('iuxmarkets.active.account');

    try {
      // Initialize server and account if not present
      if (!server) {
        StorageService.setItem('trade.server', data.data[0]?.api_server);
      }
      if (!acc) {
        StorageService.setItem('iuxmarkets.active.account', data.data[0]?.id);
      }

      let state;
      if (tk) {
        const me = await getMe();
        const listArray = await lisArray.getListArray(request, { Authorization: 'Bearer ' + tk });
        state = {
          isAuthenticated: true,
          isInitialized: false,
          metatrader: data.data.sort((a, b) => {
            // Sort real accounts first, then by balance
            if (a.type === 'real' && b.type !== 'real') return -1;
            if (a.type !== 'real' && b.type === 'real') return 1;
            return b.current_balance - a.current_balance;
          }),
          activeAccount: acc,
          accActiveData: data.data.find((item) => item.id === +acc),
          user: me.data,
          listArray
        };
      } else {
        const working =
          data.data?.sort((a, b) => {
            // Sort real accounts first, then by balance
            if (a.type === 'real' && b.type !== 'real') return -1;
            if (a.type !== 'real' && b.type === 'real') return 1;
            return b.current_balance - a.current_balance;
          }) ?? [];
        if (working.length > 0) {
          state = await getTokenFromAuth(working, token, data);
        } else {
          state = {
            isAuthenticated: true,
            isInitialized: false,
            metatrader: [],
            activeAccount: null,
            accActiveData: null,
            listArray: []
          };
        }
      }

      dispatch({
        type: 'INITIALIZE',
        payload: state
      });
    } catch (error) {
      console.error('Initialize error:', error);
      await logout();
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          isInitialized: false,
          metatrader: [],
          activeAccount: null,
          accActiveData: null,
          isOtp: false
        }
      });
    }
  };

  const authWithToken = async ({ token, loading }) => {
    loading(true);
    await signInWithCustomToken(auth, token)
      .then(async (userCredential) => {
        const user = userCredential.user;
        await accountTradeApi()
          .then(async ({ data }) => {
            await initialize(
              await user.getIdToken(),
              data.data.filter((account) => account.account_type !== 'Cent')
            );
          })
          .catch((e) => {
            if (e.response?.data?.code === 1010) {
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: false,
                  isInitialized: false,
                  metatrader: [],
                  activeAccount: null,
                  accActiveData: null,
                  isOtp: true
                }
              });
            }
          });
      })
      .catch((error) => {
        console.error('Error signing in on domain B:', error);
        loading(false);
      });
  };

  const login = async ({ username, password }) => {
    dispatch({
      type: 'LOADING',
      payload: {
        isLoading: true
      }
    });

    await signInWithEmailAndPassword(auth, username, password)
      .then(async (userCredential) => {})
      .catch((error) => {
        dispatch({
          type: 'LOADING',
          payload: {
            isLoading: false
          }
        });
        const errorCode = error.code;
        switch (errorCode) {
          case 'auth/user-not-found':
            Notification({
              message: 'Incorrect email or password. Please check your credentials and try again.',
              type: 'danger'
            });
            break;
          case 'auth/wrong-password':
            Notification({
              message: 'Incorrect email or password. Please check your credentials and try again.',
              type: 'danger'
            });
            break;
          case 'auth/invalid-email':
            Notification({
              message: 'Incorrect email or password. Please check your credentials and try again.',
              type: 'danger'
            });
            break;
          default:
            return Notification({
              message: 'Something went wrong',
              type: 'danger'
            });
        }
      });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('', {
      email,
      password,
      firstName,
      lastName
    });
    const { user, metatrader } = response.data;

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
        metatrader
      }
    });
  };

  const logout = async () => {
    await logoutFirebase();
    accountTradeData = null;
    localStorage.removeItem('trade.token');
    dispatch({
      type: 'INITIALIZE',
      payload: {
        ...initialState,
        isInitialized: false
      }
    });
    if (window.electron) {
      window.electron?.store.delete('token');
    }
  };

  const getCountry = async () => {
    try {
      const response = await fetch('https://portal.iux.com/api/country');
      if (!response.ok) {
        throw new Error('Failed to fetch country');
      }
      const data = await response.json();
      setLocation(data?.country);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  useEffect(() => {
    const initializeAuth = async (user) => {
      if (!user) {
        dispatch({
          type: 'LOADING',
          payload: { isLoading: false }
        });
        await logout();
        dispatch({
          type: 'INITIALIZE',
          payload: { ...initialState, isInitialized: false }
        });
        return;
      }

      try {
        const userRegulation = await getUserRegulation(user.uid);
        await RegulationHelper.getInstance().setRegulationId(userRegulation.regulation_id);

        // Fetch account trade data only once
        if (!accountTradeData) {
          const { data } = await accountTradeApi();
          if (data.data.length > 0) {
            accountTradeData = {
              data: data?.data.filter((account) => account.account_type !== 'Cent')
            };
          } else {
            accountTradeData = null;
          }
        }

        await handleTradeData(user, accountTradeData);
      } catch (e) {
        if (e.response?.data?.code === 1010) {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              ...initialState,
              isInitialized: false,
              isOtp: true
            }
          });
        }
      }
    };

    // getCountry();
    const unsubscribe = auth.onAuthStateChanged(initializeAuth);
    return () => {
      unsubscribe();
      accountTradeData = null;
    };
  }, []);

  const reState = async () => {
    await logout();
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: false,
        isInitialized: false,
        metatrader: [],
        activeAccount: null,
        accActiveData: null,
        isOtp: false
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        location,
        reState,
        login,
        logout,
        register,
        authWithToken,
        getFirebaseCustom
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
